import { Label } from "../UI/label";
import { useCurrency } from "../../Hooks/useCurrency";
import React from "react";
import { useSelector } from "react-redux";

const formatWithCommas = (val, countryID = 1) => {
  if (val === null || val === undefined || val === '') return '';
  const str = String(val).replace(/,/g, '');
  if (isNaN(str) || str === '') return str;

  const parts = str.split('.');
  const intPart = parts[0];
  const decimalPart = parts.length > 1 ? '.' + parts[1] : '';

  if (!intPart && intPart !== '0') return str;

  const locale = Number(countryID) === 1 ? 'en-IN' : 'en-US';
  try {
    const formattedInt = Number(intPart).toLocaleString(locale);
    return formattedInt + decimalPart;
  } catch (err) {
    return str;
  }
};

const getColorStyles = (cKey) => {
  if (!cKey || cKey === "gray" || cKey === "default") return null;
  const key = cKey.toLowerCase();
  switch (key) {
    case "green":
    case "sahi-green":
      return {
        symbolBg: "var(--sahi-green-light)",
        symbolColor: "var(--sahi-green)",
        borderColor: "var(--sahi-green)",
      };
    case "blue":
    case "dark-blue":
    case "sahi-dark-blue":
      return {
        symbolBg: "rgba(13, 31, 45, 0.12)",
        symbolColor: "var(--sahi-dark-blue)",
        borderColor: "var(--sahi-dark-blue)",
      };
    case "orange":
    case "sahi-orange":
      return {
        symbolBg: "var(--sahi-orange-light)",
        symbolColor: "var(--sahi-orange)",
        borderColor: "var(--sahi-orange)",
      };
    default:
      return {
        symbolBg: `${cKey}1c`,
        symbolColor: cKey,
        borderColor: cKey,
      };
  }
};

const BusinessInputCustom = React.forwardRef(
  (
    {
      fieldID,
      label,
      placeholder,
      value,
      onChange,
      required = false,
      maxDecimalPlaces = 2,
      maxIntegerDigits = 12,
      className = "space-y-1.5",
      disabled = false,
      onBlur,
      countryID: propCountryID,
      color,
      accentColor,
    },
    ref
  ) => {
    const currencySymbol = useCurrency();
    const agencyData = useSelector((state) => state.agencyData?.data);
    const countryID = propCountryID !== undefined ? propCountryID : (agencyData?.CountryID || 1);

    const activeColorStyle = getColorStyles(color || accentColor);

    const handleInputChange = (e) => {
      const val = e.target.value;
      const rawVal = val.replace(/,/g, "");
      // Create dynamic regex based on props
      const regex = new RegExp(
        `^\\d{0,${maxIntegerDigits}}(\\.\\d{0,${maxDecimalPlaces}})?$`
      );
      if (regex.test(rawVal) || rawVal === "") {
        e.target.value = rawVal;
        onChange(e);
      }
    };

    const showYearSuffix = fieldID === "businessPotential";
    const displayValue = formatWithCommas(value, countryID);

    return (
      <div key={fieldID} className={className}>
        {label && (
          <Label htmlFor={fieldID} required={required}>
            {label}
          </Label>
        )}
        <div className="flex items-center gap-2">
          <div
            ref={ref} // Attach ref to the outer div
            tabIndex={0} // Make the div focusable
            style={activeColorStyle ? { borderColor: activeColorStyle.borderColor } : {}}
            className={`sahi-input border flex w-full shadow-xs h-11 sahi-radius overflow-hidden ${
              disabled ? "opacity-50 cursor-not-allowed" : ""
            } ${!disabled ? "focus:ring-2 focus:ring-blue-500 focus:outline-none" : ""}`} // Focus styling
            onFocus={(e) => e.currentTarget.querySelector("input")?.focus()} // Delegate focus to input on div focus
          >
            <span
              className={`h-11 w-10 flex items-center justify-center px-3 shrink-0 font-medium ${
                !activeColorStyle ? "bg-background border-r border-gray-300" : "border-r"
              }`}
              style={
                activeColorStyle
                  ? {
                      backgroundColor: activeColorStyle.symbolBg,
                      borderColor: activeColorStyle.borderColor,
                      color: activeColorStyle.symbolColor,
                    }
                  : {}
              }
              aria-hidden="true"
            >
              {currencySymbol}
            </span>
            <input
              id={fieldID}
              name={fieldID}
              value={displayValue}
              onChange={handleInputChange}
              onBlur={onBlur}
              placeholder={placeholder}
              disabled={disabled}
              className="flex-1 border-0 focus:ring-0 focus:outline-none bg-transparent px-3 h-full placeholder:text-muted-foreground text-sm min-w-0 disabled:cursor-not-allowed"
              type="text"
              maxLength="24"
              autoComplete="off"
              inputMode="decimal"
            />
          </div>
          {showYearSuffix && (
            <span className="text-sm text-black-500 whitespace-nowrap flex-shrink-0">
              / yr
            </span>
          )}
        </div>
      </div>
    );
  }
);

BusinessInputCustom.displayName = "BusinessInputCustom";
export default BusinessInputCustom;
